import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { secureApi } from '../../api/secure';
import { logger } from '../../utils/logger';
import { dashboardKeys } from './dashboardKeys';
import { CategorizedDashboardTasks, DashboardData, DashboardFiltersState } from './dashboardTypes';
import { applyDashboardFilters, categorizeDashboardTasks } from './dashboardUtils';
import { TaskItem } from '../../components/dashboard/types/dashboardTypes';
import { FormattedTask } from './taskTypes';
import { useUpdateTask, useDeleteTask } from './useTasks';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import {
  invalidateQuery,
  safeUpdateQueryData,
  batchInvalidateQueries,
} from '../../utils/queryInvalidationUtils';
import { taskKeys } from './taskKeys';
import { cacheUsers } from '../../utils/userCache';

/**
 * Hook for checking if dashboard is relevant for the user
 *
 * This determines if user has any dashboard-relevant content to show
 */
export function useDashboardRelevance() {
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: dashboardKeys.relevance(),
    queryFn: async () => {
      try {
        const isRelevant = await secureApi.checkDashboardRelevance();
        return isRelevant;
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'checkDashboardRelevance' },
        });
        throw appError;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once since this is a non-critical check
  });
}

/**
 * Hook for fetching dashboard content
 * Returns raw dashboard data without filtering
 */
export function useDashboardContent() {
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: dashboardKeys.content(),
    queryFn: async () => {
      try {
        const startTime = performance.now();
        logger.fetch('Fetching dashboard content');

        // Fetch dashboard content from API
        const dashboardContent: DashboardData = await secureApi.getDashboardContent();

        // Log raw task for debugging format
        logger.fetch('Raw dashboard task format for debugging', {
          sampleTask:
            dashboardContent.tasks && dashboardContent.tasks.length > 0
              ? dashboardContent.tasks[0]
              : 'No tasks found',
          hasHighlighted: dashboardContent.tasks?.some(t => t.is_highlighted === true),
          hasCamelHighlighted: dashboardContent.tasks?.some(t => t.isHighlighted === true),
          taskCount: dashboardContent.tasks?.length || 0,
        });

        // Cache users for use throughout the application
        if (dashboardContent.users.length > 0) {
          cacheUsers(dashboardContent.users);
        }

        // Add dashboardSection to each task for drag and drop
        let rawTasks = dashboardContent.tasks || [];

        // Format the tasks to FormattedTask format
        rawTasks = rawTasks
          .filter(task => task.status !== 'done')
          .map(task => {
            // Convert the task to a FormattedTask format or at least ensure it has proper properties
            return {
              ...task,
              // Ensure we're using the right property names for dashboard
              isHighlighted: task.is_highlighted === true || task.isHighlighted === true,
              assignedTo: task.assigned_to || task.assignedTo || null,
              // Set initial section to thisWeek - this will be re-categorized later
              dashboardSection: 'thisWeek',
            };
          });

        // Now properly categorize each task
        const categorized = categorizeDashboardTasks(rawTasks as any);

        // Update dashboard section for each task based on the categorization
        rawTasks = rawTasks.map(task => {
          // Find which section contains this task
          if (categorized.highlightedTasks.some(t => t.id === task.id)) {
            task.dashboardSection = 'highlighted';
          } else if (categorized.overdueTasks.some(t => t.id === task.id)) {
            task.dashboardSection = 'overdue';
          } else if (categorized.todayTasks.some(t => t.id === task.id)) {
            task.dashboardSection = 'today';
          } else {
            task.dashboardSection = 'thisWeek';
          }
          return task;
        });

        // Update projects with isShared flag
        const projects = dashboardContent.projects.map(project => ({
          ...project,
          isShared: false, // Default to false, will be updated by project store
        }));

        const endTime = performance.now();
        logger.fetch(`Dashboard content fetched in ${(endTime - startTime).toFixed(2)}ms`);

        return {
          tasks: rawTasks,
          projects,
          users: dashboardContent.users,
        };
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchDashboardContent' },
        });
        throw appError;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for filtered dashboard content
 *
 * @param filters - Dashboard filters to apply
 * @param currentUserId - Current user ID
 * @param projectIsSharedFunc - Function to determine if a project is shared
 * @returns The dashboard content query with filtered data
 */
export function useFilteredDashboardContent(
  filters: DashboardFiltersState,
  currentUserId: string | null,
  projectIsSharedFunc: (projectId: string) => boolean
): {
  data: DashboardData | undefined;
  filteredData: CategorizedDashboardTasks | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
} {
  const dashboardContentQuery = useDashboardContent();

  // Calculate filtered and categorized tasks
  const filteredData = useMemo(() => {
    if (!dashboardContentQuery.data) {
      // Return empty categorized tasks instead of null to prevent runtime errors
      return {
        highlightedTasks: [],
        overdueTasks: [],
        todayTasks: [],
        thisWeekTasks: [],
        availableTags: [],
      };
    }

    return calculateFilteredDashboard(
      dashboardContentQuery.data.tasks,
      filters,
      currentUserId,
      projectIsSharedFunc
    );
  }, [
    dashboardContentQuery.data,
    filters.userIds,
    filters.projectIds,
    filters.tags,
    currentUserId,
    projectIsSharedFunc,
  ]);

  return {
    ...dashboardContentQuery,
    filteredData,
  };
}

/**
 * Helper function to calculate filtered and categorized dashboard tasks
 */
function calculateFilteredDashboard(
  tasks: (TaskItem & Partial<FormattedTask>)[], // Accept hybrid type for transition
  filters: DashboardFiltersState,
  currentUserId: string | null,
  projectIsSharedFunc: (projectId: string) => boolean
): CategorizedDashboardTasks {
  // Ensure all tasks have FormattedTask properties
  const formattedTasks = tasks.map(task => ({
    ...task,
    // Make sure we have correct property names for both formats during transition
    isHighlighted: task.is_highlighted === true || task.isHighlighted === true,
    assignedTo: task.assigned_to || task.assignedTo || null,
  }));

  // Apply filters
  const filteredTasks = applyDashboardFilters(
    formattedTasks as FormattedTask[],
    filters,
    currentUserId,
    projectIsSharedFunc
  );

  // Categorize tasks
  return categorizeDashboardTasks(filteredTasks);
}

/**
 * Hook for updating a task in the dashboard
 */
export function useUpdateDashboardTask() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();
  const updateTaskMutation = useUpdateTask();

  return useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: Partial<TaskItem> }) => {
      try {
        logger.store('Dashboard updating task', {
          taskId,
          updates,
          is_online: isOnline,
        });

        // Find the task to get the topic ID
        const dashboardData = queryClient.getQueryData<DashboardData>(dashboardKeys.content());
        if (!dashboardData) {
          throw new Error('Dashboard data not available');
        }

        const task = dashboardData.tasks.find(t => t.id === taskId);
        if (!task) {
          throw new Error(`Task ${taskId} not found`);
        }

        // Extract the topic_id from the task's topics field
        const topicInfo = task.topics as { project_id: string; id: string };
        if (!topicInfo || !topicInfo.id) {
          throw new Error('Topic info not found for task');
        }

        const topicId = topicInfo.id;

        // Update the task via React Query
        await updateTaskMutation.mutateAsync({
          topicId,
          taskId,
          updates,
        });

        return { taskId, topicId, updates };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateDashboardTask', taskId },
        });
        throw appError;
      }
    },
    onMutate: async ({ taskId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: dashboardKeys.content() });

      // Get current dashboard data
      const previousDashboardData = queryClient.getQueryData<DashboardData>(
        dashboardKeys.content()
      );

      if (previousDashboardData) {
        // Optimistically update the dashboard tasks
        const updatedTasks = previousDashboardData.tasks.map(task => {
          if (task.id === taskId) {
            // For updated tasks, determine if we need to recalculate the dashboardSection
            const updatedTask = { ...task, ...updates };

            // If task is now marked as 'done', exclude it from dashboard
            if (updatedTask.status === 'done') {
              return updatedTask; // Will be filtered out during categorization
            }

            // Recalculate dashboardSection
            const categorized = categorizeDashboardTasks([updatedTask]);
            updatedTask.dashboardSection =
              categorized.highlightedTasks.length > 0
                ? 'highlighted'
                : categorized.overdueTasks.length > 0
                  ? 'overdue'
                  : categorized.todayTasks.length > 0
                    ? 'today'
                    : 'thisWeek';

            return updatedTask;
          }
          return task;
        });

        // Update dashboard data in cache
        queryClient.setQueryData<DashboardData>(dashboardKeys.content(), {
          ...previousDashboardData,
          tasks: updatedTasks,
        });
      }

      return { previousDashboardData };
    },
    onError: (error, variables, context) => {
      logger.error('Failed to update task in dashboard', error);

      // Roll back to previous dashboard data on error
      if (context?.previousDashboardData) {
        queryClient.setQueryData(dashboardKeys.content(), context.previousDashboardData);
      }
    },
    onSuccess: result => {
      // The underlying updateTaskMutation already handles all necessary invalidations
      // We only need to invalidate dashboard-specific queries

      const { taskId } = result;

      // Only invalidate dashboard content to refresh categorization
      // Use 'active' refetchType to ensure data is refetched
      invalidateQuery(queryClient, dashboardKeys.content(), {
        exact: true,
        refetchType: 'active',
        context: { operation: 'updateDashboardTask', taskId, reason: 'task_updated' },
      });
    },
  });
}

/**
 * Hook for deleting a task in the dashboard
 */
export function useDeleteDashboardTask() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();
  const deleteTaskMutation = useDeleteTask();

  return useMutation({
    mutationFn: async ({
      taskId,
      topicId: providedTopicId,
    }: {
      taskId: string;
      topicId?: string;
    }) => {
      try {
        logger.store('Dashboard deleting task', {
          taskId,
          topicId: providedTopicId,
          is_online: isOnline,
        });

        let topicId = providedTopicId;

        // If topicId wasn't provided, try to find it from the cache (fallback)
        if (!topicId) {
          const dashboardData = queryClient.getQueryData<DashboardData>(dashboardKeys.content());
          if (dashboardData) {
            const task = dashboardData.tasks.find(t => t.id === taskId);
            if (task) {
              const topicInfo = task.topics as { project_id: string; id: string } | undefined;
              topicId = topicInfo?.id;
            }
          }
        }

        if (!topicId) {
          throw new Error(
            `Cannot delete task ${taskId}: topic ID not found. Please refresh and try again.`
          );
        }

        // Delete the task via React Query
        await deleteTaskMutation.mutateAsync({
          topicId,
          taskId,
        });

        return { taskId, topicId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteDashboardTask', taskId },
        });
        throw appError;
      }
    },
    onMutate: async ({ taskId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: dashboardKeys.content() });

      // Get current dashboard data
      const previousDashboardData = queryClient.getQueryData<DashboardData>(
        dashboardKeys.content()
      );

      // IMPORTANT: Capture the task data NOW before it gets removed from cache by useDeleteTask.onMutate
      // This is needed because we reference task data in onSuccess for cache invalidation
      const taskToDelete = previousDashboardData?.tasks.find(t => t.id === taskId);

      if (previousDashboardData) {
        // Optimistically remove the task
        const updatedTasks = previousDashboardData.tasks.filter(task => task.id !== taskId);

        // Update dashboard data in cache
        queryClient.setQueryData<DashboardData>(dashboardKeys.content(), {
          ...previousDashboardData,
          tasks: updatedTasks,
        });
      }

      return { previousDashboardData, taskToDelete };
    },
    onError: (error, variables, context) => {
      logger.error('Failed to delete task in dashboard', error);

      // Roll back to previous dashboard data on error
      if (context?.previousDashboardData) {
        queryClient.setQueryData(dashboardKeys.content(), context.previousDashboardData);
      }
    },
    onSuccess: (result, _variables, context) => {
      const { taskId, topicId } = result;

      // Use task data captured in onMutate (before useDeleteTask removed it from cache)
      // This avoids the race condition where the cache is cleared before we can read it
      const task = context?.taskToDelete;

      // Remove task from detail cache (may already be gone, but ensure cleanup)
      queryClient.removeQueries({ queryKey: taskKeys.detail(taskId), exact: true });

      // Also remove from dashboard task cache
      queryClient.removeQueries({ queryKey: dashboardKeys.task(taskId), exact: true });

      // Update task lists by filtering out the deleted task
      safeUpdateQueryData<TaskItem[]>(
        queryClient,
        taskKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return oldData;
          return oldData.filter(t => t.id !== taskId);
        },
        { context: { operation: 'deleteDashboardTask', taskId, topicId } }
      );

      // Prepare batch invalidation for related queries
      const invalidationMap = new Map();

      // If the task had an assignee, add invalidation for user-specific dashboard views
      // Check both camelCase and snake_case since task may have either format
      const assignedTo = task?.assignedTo || task?.assigned_to;
      if (assignedTo) {
        invalidationMap.set(dashboardKeys.byUser(assignedTo), {
          refetchType: 'none',
          context: { operation: 'deleteDashboardTask', userId: assignedTo },
        });
      }

      // Determine which dashboard section the task was in, if any
      if (task) {
        const dashboardSection = task.dashboardSection;
        if (dashboardSection) {
          invalidationMap.set(dashboardKeys.section(dashboardSection), {
            refetchType: 'none',
            context: { operation: 'deleteDashboardTask', section: dashboardSection },
          });
        } else {
          // If we don't know the section, invalidate all sections
          invalidationMap.set(dashboardKeys.section(), {
            refetchType: 'none',
            context: { operation: 'deleteDashboardTask', cause: 'unknown' },
          });
        }
      }

      // Add dashboard content invalidation
      invalidationMap.set(dashboardKeys.content(), {
        refetchType: 'none',
        context: { operation: 'deleteDashboardTask' },
      });

      // Batch invalidate all queries if we have any to invalidate
      if (invalidationMap.size > 0) {
        batchInvalidateQueries(queryClient, invalidationMap, {
          refetchType: 'none',
        });
      }
    },
  });
}
